import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Sun, Moon, Phone, MessageCircle, MapPin, Facebook as FacebookIcon, Instagram as InstagramIcon } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { CONFIG } from '../config/constants'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/team', label: 'Team' },
    { path: '/exercises', label: 'Exercises' },
    { path: '/blog', label: 'Blog' },
    { path: '/testimonials', label: 'Reviews' },
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-8xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center flex-shrink-0"
              >
                {/* Actual Logo Image with Transparent Background */}
                <img
                  src="/logo.png"
                  alt="BODY SCIENCE REHAB Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // Fallback to a simple text logo if image fails to load
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'block';
                    }
                  }}
                />
                {/* Fallback Logo */}
                <div className="hidden w-full h-full bg-gradient-to-br from-orange-400 to-blue-500 rounded-xl flex items-center justify-center text-white font-bold text-xs">
                  BSR
                </div>
              </motion.div>
              <div className="min-w-0 hidden sm:block">
                <h1 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white leading-tight truncate">
                  BODY SCIENCE REHAB
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                  Physiotherapy & Rehabilitation
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2 xl:space-x-4 ml-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link text-xs xl:text-sm ${
                    location.pathname === item.path ? 'active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Action Buttons */}
            <div className="flex items-center space-x-1 sm:space-x-2 ml-2 sm:ml-4">
              {/* Theme Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className="icon-btn w-8 h-8 sm:w-9 sm:h-9"
              >
                {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
              </motion.button>

              {/* Quick Actions */}
              <div className="hidden md:flex items-center space-x-1">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={`tel:${CONFIG.phone}`}
                  className="action-btn call-btn px-2 py-1.5"
                >
                  <Phone size={14} />
                  <span className="hidden xl:inline">Call</span>
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={`https://wa.me/${CONFIG.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn whatsapp-btn px-2 py-1.5"
                >
                  <MessageCircle size={14} />
                  <span className="hidden xl:inline">WhatsApp</span>
                </motion.a>
              </div>

              {/* Book Now Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="hidden sm:block"
              >
                <button
                  onClick={() => navigate('/bookings')}
                  className="btn btn-primary text-xs sm:text-sm px-3 py-2 sm:px-4 sm:py-2.5"
                >
                  <span className="hidden sm:inline">Book Now</span>
                  <span className="sm:hidden">Book</span>
                </button>
              </motion.div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="container py-4">
                <nav className="flex flex-col space-y-2">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        location.pathname === item.path
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col space-y-2">
                      <a
                        href={`tel:${CONFIG.phone}`}
                        className="flex items-center space-x-2 px-4 py-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
                      >
                        <Phone size={16} />
                        <span>Call Us</span>
                      </a>
                      <a
                        href={`https://wa.me/${CONFIG.whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 px-4 py-3 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                      >
                        <MessageCircle size={16} />
                        <span>WhatsApp</span>
                      </a>
                    </div>
                  </div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Main Content */}
      <main className="pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="container section-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img
                    src="/logo.png"
                    alt="BODY SCIENCE REHAB Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      // Fallback to a simple text logo if image fails to load
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'block';
                      }
                    }}
                  />
                  {/* Fallback Logo */}
                  <div className="hidden w-full h-full bg-gradient-to-br from-orange-400 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    BSR
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold">{CONFIG.businessName}</h3>
                  <p className="text-sm text-gray-400">Advanced Rehabilitation</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Advanced Physiotherapy & Accurate Diagnosis.
              </p>
              <div className="flex space-x-4">
                {/* Google (Logo) */}
                <a href="https://share.google/PbugAfjYFFQJtQa3P" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Google">
                  <img src="https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png" alt="Google" className="h-4 w-auto" />
                </a>
                {/* WhatsApp */}
                <a href={`https://wa.me/${CONFIG.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="WhatsApp">
                  <MessageCircle className="w-5 h-5" />
                </a>
                {/* Instagram */}
                <a href="https://www.instagram.com/bodysciencerehab/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <InstagramIcon className="w-5 h-5" />
                </a>
                {/* Google Maps */}
                <a href="https://maps.app.goo.gl/pLabKhoSnhq9MVH8A" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Google Maps">
                  <MapPin className="w-5 h-5" />
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/p/Body-Science-Rehab-61574156604095/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <FacebookIcon className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Advanced Physiotherapy</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Sports Rehabilitation</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Home Care</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Tele-Rehab</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors">Chiropractic Care</Link></li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="/team" className="text-gray-400 hover:text-white transition-colors">Our Team</a></li>
                <li><a href="/exercises" className="text-gray-400 hover:text-white transition-colors">Exercise Library</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="/testimonials" className="text-gray-400 hover:text-white transition-colors">Reviews</a></li>
                <li><a href="/bookings" className="text-gray-400 hover:text-white transition-colors font-medium">📋 Book Appointment</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Phone size={16} className="text-primary-400" />
                  <a href={`tel:${CONFIG.phone}`} className="text-gray-400 hover:text-white transition-colors">
                    {CONFIG.phone}
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageCircle size={16} className="text-primary-400" />
                  <a href={`mailto:${CONFIG.email}`} className="text-gray-400 hover:text-white transition-colors">
                    {CONFIG.email}
                  </a>
                </div>
                <p className="text-gray-400">{CONFIG.address}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} {CONFIG.businessName}. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">
                Privacy Policy
              </a>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">
                Terms of Service
              </a>
              <a href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
